import axios from 'axios';
import Locale from 'locale-codes';

// Need to import this dynamically otherwise it becomes a circular dependency and will throw errors
const i18n = import('@/util/language');
const config = require('../../config.json');
const apiUrl = config.api_url;

function sortByName({ name: aName = '' }, { name: bName = '' }) {
  return aName.localeCompare(bName);
}

export default {
  state: () => ({
    userLocale: '',
    supportedLanguages: [],
    loadedLanguages: [],
  }),
  getters: {
    userLocale: (state) => {
      const { userLocale } = state;
      return state.supportedLanguages.find(({ code }) => code === userLocale);
    },
    supportedLanguages: state => state.supportedLanguages.sort(sortByName),
  },
  mutations: {
    setUserLocale(state, locale) {
      state.userLocale = locale;
    },
    setSupportedLanguages(state, languages) {
      state.supportedLanguages = languages || [];
    },
    addLoadedLanguage(state, language) {
      state.loadedLanguages.push(language);
    },
  },
  actions: {
    async setUserLocale({ state, commit, dispatch }, locale) {
      if (locale) {
        dispatch('fetchLanguage', locale);
        commit('setUserLocale', locale);

        return;
      }

      if (state.userLocale) {
        dispatch('fetchLanguage', state.userLocale);
        return;
      }

      const navigatorLanguages = [navigator.language, ...(navigator.languages || [])];
      const supportedLanguages = state.supportedLanguages?.map(({ code }) => code) || [];

      let language = 'en';

      const userDefaultLanguage = navigatorLanguages
        .find((code) => {
          if (supportedLanguages.includes(code)) {
            return true;
          }

          const { 'iso639-1': iso639v1 } = Locale.getByTag(code);

          return supportedLanguages.includes(iso639v1);
        });

      if (userDefaultLanguage) {
        if (supportedLanguages.includes(userDefaultLanguage)) {
          language = userDefaultLanguage;
        } else {
          language = Locale.getByTag(userDefaultLanguage)['iso639-1'];
        }
      }

      dispatch('fetchLanguage', language);
      commit('setUserLocale', language);
    },
    async fetchLanguages({ commit, dispatch }) {
      let languages = {};

      try {
        ({ data: { languages } } = await axios.get(`${apiUrl}/data/translations`));
      } catch (error) {
        console.error('Failed to load supported languages from metadata API.', error);
        commit('setSupportedLanguages', [{
          code: 'en',
          name: 'English',
          countryCode: 'gb',
        }]);
        dispatch('setUserLocale', 'en');
        return;
      }

      languages.en = {
        code: 'en', name: 'English', localeTag: 'en_GB', progressWeb: 100,
      };

      const languageFilter = (lang) => {
        const language = languages[lang];

        if (language?.progressWeb) {
          return language.progressWeb;
        }

        return false;
      };

      const languageMap = (code) => {
        const { name, localeTag } = languages[code];

        let [, countryCode] = localeTag.split('_');
        countryCode = countryCode.toLowerCase();

        // Serbia doesn't get the right ISO code
        if (countryCode === 'cs') {
          countryCode = 'rs';
        }

        /*
        const flagUrl = localeTag === 'en_PT'
          ? '/twemoji-pirate-flag.png'
          : `https://flagcdn.com/w40/${locale}.png`;
        */

        return {
          code,
          name,
          countryCode,
        };
      };

      const langToArray = Object.keys(languages);
      const supportedLanguages = langToArray.filter(languageFilter).map(languageMap);

      commit('setSupportedLanguages', supportedLanguages);
      dispatch('setUserLocale');
    },
    async fetchLanguage(_, locale) {
      const VueI18n = (await i18n).default;

      if (locale === 'en') {
        VueI18n.locale = locale;
        return;
      }

      try {
        const { data } = await axios.get(`${apiUrl}/translation/web/${locale}`);

        VueI18n.locale = locale;
        VueI18n.setLocaleMessage(locale, data);
      } catch (error) {
        console.error(`Failed to load locale "${locale}" from metadata API. Falling back to English.`, error);
        VueI18n.locale = 'en';
      }
    },
  },
};
